import Layout from "../../components/layout";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import nextI18NextConfig from "../../next-i18next.config";
import Accordion from "../../components/v2/accordion/accordion";
import Link from "../../components/Link";
import CallToActionSmall from "../../components/v2/call-to-action-sm";
import { GetStaticProps } from "next";
import ComparisonForm from "../../components/v2/form/comparison-form";
import { useTranslation } from "next-i18next";
import Head from "next/head";
import dynamic from "next/dynamic";
import { useState } from "react";
import "../../styles/scss/pricing.module.scss";
import '../../styles/scss/_faq-section.module.scss';
import "../../styles/scss/solutions.module.scss";
import TrustedCompaniesV2 from "../../components/v2/trusted-companies-v2";
import VendorComparisonGrid from "../../components/v2/comparison/vendor-comparison-grid";
import VendorComparisonTable from "../../components/v2/comparison/vendor-comparison-table";
import {
    CORALOGIX_COMPARISON_SECTIONS,
    CORALOGIX_FEATURE_TABLE_ROWS,
    CORALOGIX_SWITCH_STATS,
} from "../../components/v2/comparison/coralogix-comparison-data";
import VendorSwitchStats from "../../components/v2/comparison/vendor-switch-stats";
import CallToActionDocsLink from "../../components/v2/blog/call-to-action-docs-link";
import CardWithIcon from "../../components/v2/cards/card-with-icon";
import ComparisonVideoTestimonial from "../../components/v2/comparison/comparison-video-testimonial";
import { AIK8sRootIconDark, AutoFixIconDark, OpsAIDarkIcon, AIDashboardIconDark, AIAlertIconDark, ZeroReinstrumentationIcon, IncrementalAdoptionIcon, DataContinuityIcon } from "../../components/v2/icons-v2";

const Dialog = dynamic(() => import("../../components/v2/dialog"), {
    ssr: false,
});

export default function CoralogixVsMiddleware({ }) {
    const { t } = useTranslation('common');
    const data = t('comparison.coralogix', { returnObjects: true, defaultValue: {} }) as any;

    const [testimonialVideoOpen, setTestimonialVideoOpen] = useState(false);
    const [testimonialVideoSrc, setTestimonialVideoSrc] = useState<string | null>(null);

    const handleTestimonialVideoOpen = (src: string) => {
        setTestimonialVideoSrc(src);
        setTestimonialVideoOpen(true);
    };

    const formData = [
        {
            id: 1,
            header: "Zero Re-instrumentation",
            icon: <ZeroReinstrumentationIcon />,

        },
        {
            id: 2,
            header: "Incremental Adoption",
            icon: <IncrementalAdoptionIcon />,

        },
        {
            id: 3,
            header: "100% Data Continuity",
            icon: <DataContinuityIcon />,

        },
    ]

    const faqs = [
        {
            id: 1,
            header: "How is Middleware's pricing different from Coralogix's?",
            text: (
                <p>Both bill on data size with no per-host or per-user fees. The difference is what you have to do to keep the bill predictable. Coralogix relies on TCO Optimizer policies you configure and maintain. Middleware bills on what you send, with two-click drop rules if you want to cut volume further.</p>
            ),
        },
        {
            id: 2,
            header: "Do I still get cost control without TCO Optimizer?",
            text: (
                <p>Yes. Ingestion Control lets you drop sources, fields, and high-cardinality dimensions in two clicks. No policy maintenance required.</p>
            ),
        },
        {
            id: 3,
            header: "Is there a free trial?",
            text: (
                <p>14 days, every feature, no credit card.</p>
            ),
        },
        {
            id: 4,
            header: "Is Middleware OpenTelemetry-native?",
            text: (
                <p>
                    Yes. OTel is the foundation, not a connector. No proprietary engine, query language, or pipeline syntax to learn.
                </p>
            ),
        },
        {
            id: 5,
            header: "Do I need to learn a new query language?",
            text: (
                <p>
                    No. Most workflows don't require one. Coralogix needs DataPrime or Lucene for anything non-trivial.
                </p>
            ),
        },
        {
            id: 6,
            header: "What about long-term retention?",
            text: (
                <p>
                    Middleware includes 180 days. Coralogix relies on archiving to your S3 bucket and using Remote Query, which is powerful but adds operational complexity.
                </p>
            ),
        },
        {
            id: 7,
            header: "Can I deploy on-premises or BYOC? ",
            text: (
                <p>
                    Yes — cloud, on-prem, BYOC, or hybrid.
                </p>
            ),
        },
        {
            id: 8,
            header: "What happens to my Coralogix dashboards?",
            text: (
                <p>
                    We help you rebuild critical dashboards in Middleware during onboarding.
                </p>
            ),
        },
        {
            id: 9,
            header: "Can I keep my existing OTel instrumentation?",
            text: (
                <p>
                    Yes. Repoint your collectors at Middleware and ingest immediately.
                </p>
            ),
        },
    ];



    const softwareApplicationSchema = {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "Middleware Observability Platform",
        "url": "https://middleware.io/",
        "applicationCategory": "DeveloperApplication",
        "operatingSystem": "Web",
        "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.7",
            "reviewCount": "320"
        }
    };

    const webPageSchema = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "@id": "https://middleware.io/comparison/coralogix-vs-middleware/",
        "name": "Coralogix vs Middleware – Observability Comparison",
        "description": "Compare Coralogix and Middleware across full-stack observability, OpenTelemetry, pricing, and ease of use.",
        "mainEntity": {
            "@type": "ItemList",
            "itemListOrder": "Unordered",
            "numberOfItems": 2,
            "itemListElement": [
                {
                    "@type": "ListItem",
                    "position": 1,
                    "item": {
                        "@type": "SoftwareApplication",
                        "name": "Middleware Observability Platform",
                        "url": "https://middleware.io/",
                        "applicationCategory": "DeveloperApplication",
                        "operatingSystem": "Web"
                    }
                },
                {
                    "@type": "ListItem",
                    "position": 2,
                    "item": {
                        "@type": "SoftwareApplication",
                        "name": "Coralogix",
                        "url": "https://coralogix.com/",
                        "applicationCategory": "DeveloperApplication",
                        "operatingSystem": "Web"
                    }
                }
            ]
        },
        "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD",
            "url": "https://middleware.io/"
        }
    };


    return (
        <>
            <Head>
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(softwareApplicationSchema)
                    }}
                />
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(webPageSchema)
                    }}
                />
            </Head>
            <Layout
                isHomePage={false}
                pageTitle={"Coralogix Alternative: Middleware vs Coralogix"}
                ogImage={"/images/comparison/coralogix-og.webp"}
                uri={"/comparison/coralogix-vs-middleware/"}
                metaTitle={"Coralogix Alternative: Middleware vs Coralogix"}
                metaDesc={"Compare Middleware vs Coralogix on pricing, OpenTelemetry, AI observability, retention, and cost control. Cut costs without sacrificing visibility."}
                metaRobotsNoindex={"index"}
                metaRobotsNofollow={"follow"}
            >

                <div className={"mw-vs-datadog solutions"}>
                    <section className={`hero-section solutions-child-page`}>
                        <div className={'container'}>
                            <h1>The Better Coralogix<span className='text-gradient'> Alternative</span><br /> Full-Stack Observability with an SRE Agent Built In</h1>
                            <p className="mb-4">Tired of Streama priority tiers, TCO Optimizer policy maintenance, and a log-first architecture that never quite feels unified? Switch to Middleware, an OpenTelemetry-native observability platform with predictable pricing, one-minute setup, and OpsAI — an SRE Agent that ships the fix as a pull request.</p>

                            <div className={"d-flex"}>
                                <Link href={"/demo-request/"} target={"_blank"}
                                    className={"sign-up ahref-btn primary"}>Request a Demo</Link>
                                <Link href={'#comparison-mw'} className={"sign-up ahref-btn secondary"}>See Comparison</Link>
                            </div>
                        </div>
                    </section>


                    <TrustedCompaniesV2
                        className="home-page-customer"
                        paragraph="Trusted By The Best Teams From"
                    />

                    <VendorComparisonGrid
                        sections={CORALOGIX_COMPARISON_SECTIONS}
                        competitor={{
                            name: "Coralogix",
                            logoSrc: "/images/comparison/coralogix-border-logo.svg",
                            logoAlt: "Coralogix Logo",
                            cellLogoSrc: "/images/comparison/coralogix-logo.svg",
                            headerLogoWidth: 140,
                            headerLogoHeight: 42,
                        }}
                    />

                    <CallToActionDocsLink
                        title={'Point your existing Coralogix alerts at Middleware & switch incrementally'}
                        docsLink="https://docs.middleware.io"
                    />

                    <section className="ai-native-features">
                        <div className="container">
                            <h2>The first observability platform built around AI, <br />
                                not bolted onto it</h2>

                            <section className={'mw-card career'}>
                                <div className={'d-flex card-items'}>
                                    <CardWithIcon
                                        icon={<AIDashboardIconDark />}
                                        title={"AI Dashboards"}
                                        description={
                                            "Describe what you want in plain English. Middleware builds the dashboard, picks the charts, and writes the queries. No PromQL, no custom query language, no template gallery to comb through."
                                        }
                                    />
                                    <CardWithIcon
                                        icon={<AIAlertIconDark />}
                                        title={"AI Alerts"}
                                        description={
                                            "Alerts that learn what \"normal\" looks like for each service and surface only the deviations that matter. Anomaly detection that doesn't drown your on-call rotation in noise."
                                        }
                                    />
                                    <CardWithIcon
                                        icon={<OpsAIDarkIcon />}
                                        title={"OpsAI · SRE Agent"}
                                        description={
                                            "An AI SRE that pinpoints root cause, identifies the offending line of code, and opens a pull request with the fix while you're still reading the alert. Auto-resolves issues with 80% accuracy."
                                        }
                                    />
                                    <CardWithIcon
                                        icon={<AIK8sRootIconDark />}
                                        title={"AI K8s Root Cause Finder"}
                                        description={
                                            "Auto-structuring, severity detection, and pattern recognition built into log ingestion. No manual regular expressions, no parsing pipelines to maintain."
                                        }
                                    />
                                    <CardWithIcon
                                        icon={<AutoFixIconDark />}
                                        title={"Auto-Fix · 60% Time Saved"}
                                        description={
                                            "Auto-fix across infrastructure and application layers with 80% accuracy. Engineers spend 60% less time on issue resolution — and the rest on shipping product."
                                        }
                                    />

                                </div>
                            </section>
                        </div>
                    </section>

                    <div className="customer-stories-comparison">
                        <div className="container">
                            <div className="section-tag testimonial">
                                Testimonials
                            </div>
                            <h2><span className="text-gradient">Customer </span> Stories</h2>


                            <div className="d-flex">
                                <div className="customer-story-card">
                                    <img src="/images/trusted-company/aceturtle.svg" alt="Aceturtle" />
                                    <p>Middleware has played a very good role in transforming our observability and application performance. For instance, we reduced our total observability costs by 50%.</p>
                                    <Link href="/customers/aceturtle/" className="read-more">
                                        Read the story
                                    </Link>
                                    <div className="customer-author">
                                        <p className="name">Vivek Gupta</p>
                                        <p>Founder & CEO</p>
                                    </div>
                                </div>

                                <div className="customer-story-card">
                                    <img src="/images/trusted-company/hotplate.svg" alt="Hotplate" />
                                    <p>We're heavy users of RUM and have tried many tools before discovering Middleware. We've been using OpsAI for the past few weeks, and it has automatically resolved about 60% of our production issues.</p>
                                    <Link href="/customers/hotplate/" className="read-more">
                                        Read the story
                                    </Link>
                                    <div className="customer-author">
                                        <p className="name">Rangaraj Tirumala</p>
                                        <p>Founding Engineer at Hotplate</p>
                                    </div>
                                </div>

                                <ComparisonVideoTestimonial onVideoOpen={handleTestimonialVideoOpen} />
                            </div>

                        </div>
                    </div>

                    <VendorComparisonTable
                        rows={CORALOGIX_FEATURE_TABLE_ROWS}
                        competitor={{
                            name: "Coralogix",
                            logoSrc: "/images/comparison/coralogix-logo.svg",
                            logoAlt: "Coralogix Logo",
                            cellLogoSrc: "/images/comparison/coralogix-logo.svg",
                            cellLogoWidth: 90,
                            cellLogoHeight: 22,
                            headerLogoWidth: 140,
                            headerLogoHeight: 42,
                        }}
                        description="Evaluate for yourself. Here's how Middleware stacks up against Coralogix, capability by capability."
                    />

                    <VendorSwitchStats content={CORALOGIX_SWITCH_STATS} />

                    <ComparisonForm title={"You Don't Have to Rip Out Coralogix on Day One"} label={"Coralogix"} formId={"fe366fbe-6db7-44d5-870c-e0e0acecd246"} description={"Already running Coralogix Alerts? Forward it to OpsAI and let it investigate & fix incidents against your existing stack without re-instrumentation"} isDatadog={true} formDataComparison={formData} docsLink="https://docs.middleware.io" />

                    <section className={"faq-section"}>
                        <div className={"container"}>
                            <h2>{String(data?.faq?.title ?? 'FAQs')}</h2>
                            <p>{String(data?.faq?.subtitle ?? 'Everything you want to know about the Coralogix Alternative')}</p>
                            <div className={"faq-item"}>
                                <Accordion faqs={faqs} showGroups={true} />
                            </div>
                        </div>
                    </section>

                    <CallToActionSmall />

                </div>
            </Layout>

            <Dialog
                isOpen={testimonialVideoOpen}
                position="center"
                onClose={() => {
                    setTestimonialVideoOpen(false);
                    setTestimonialVideoSrc(null);
                }}
                size="md"
                verticalCenter={true}
            >
                <div style={{ textAlign: "center" }}>
                    {testimonialVideoSrc ? (
                        <video
                            key={testimonialVideoSrc}
                            playsInline
                            preload="none"
                            autoPlay
                            width="100%"
                            height={450}
                            controls={true}
                        >
                            <source src={testimonialVideoSrc} type="video/mp4" />
                        </video>
                    ) : null}
                </div>
            </Dialog>
        </>
    );
}

export const getStaticProps: GetStaticProps = async (ctx) => {
    const translations = await serverSideTranslations(
        ctx?.locale ?? "en",
        ["common", "header", "footer"],
        nextI18NextConfig,
    );
    return {
        props: {
            ...translations,
            pageTitle: "Coralogix Alternative: Middleware vs Coralogix",
            description: "Compare Middleware vs Coralogix on pricing, OpenTelemetry, AI observability, retention, and cost control. Cut costs without sacrificing visibility.",
            ogImage: "/images/comparison/coralogix-og.webp",
            uri: "/comparison/coralogix-vs-middleware/",
            metaTitle: "Coralogix Alternative: Middleware vs Coralogix",
            metaDesc: "Compare Middleware vs Coralogix on pricing, OpenTelemetry, AI observability, retention, and cost control. Cut costs without sacrificing visibility.",
            metaRobotsNoindex: "index",
            metaRobotsNofollow: "follow",
        },
    }
}
